<script>
import { liteEloadCategoryMethods, liteEloadCategoryComputed, layoutMethods, erpUserRolesMethods, liteEloadDropdownMethods, liteEloadDropdownComputed } from '@/state/helpers';
import Swal from 'sweetalert2';
import { required } from 'vuelidate/lib/validators';
import { getModuleAccess } from '@/router/helper.js';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import InputColorPicker from 'vue-native-color-picker';
import { ToggleButton } from 'vue-js-toggle-button';
import ReorderModal from '@/components/ReorderModal.vue';
import shortid from 'shortid';
import moment from 'moment';

export default {
  page: {
    title: 'E-Load Category Form',
  },
  data() {
    return {
      categoryId: 0,
      submitted: false,
      networkOptions: [],
      proceedAction: false,
      category: {
        status: true,
        categoryName: '',
        network: null,
        hierarchy: '',
        itemCount: 0,
        special: false,
        durationDays: null,
        durationDateRange: null,
        default_category: 0,
      },
      icon1: {
        name: 'icon1',
        url: 'https://dev-crm-files.peddlr.io/eload-special-category/icons/default-icon.svg',
        default: true,
      },
      icon2: {
        name: 'icon2',
        url: '',
        default: false,
        invalidFormat: false,
      },
      color1: {
        name: 'color1',
        value: '#E83092',
        default: true,
      },
      color2: {
        name: 'color2',
        value: '#7F4697',
        default: false,
      },
      eloadCategoryModule: {
        access: {
          create: true,
          update: true,
        },
      },
      durationRangeOptions: {
        mode: 'range',
        dateFormat: 'Y-m-d',
      },
      iconFile: null,
      icon2Img: {
        filename: null,
        type: null,
        base64: null,
      },
      isForUploading: false,
      s3_url: '',
      // Reorder modal
      showReorderModal: false,
      // Logs modal data
      showLogsModal: false,
      changePreviewVisible: false,
      changePreviewData: null,
      isSearching: false,
      searchValue: {
        searchField: 'all',
        search: '',
        dateRange: '',
        page: 1,
        rows: 10,
      },
      searchLogOption: [
        { value: 'all', text: 'Search All' },
        { value: 'actor', text: 'Created By' },
      ],
      rangeOptions: {
        mode: 'range',
        dateFormat: 'Y-m-d',
      },
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      sortBy: 'created_at',
      sortDesc: true,
      fields: [
        { key: 'created_at', label: 'Date & Time', sortable: true },
        { key: 'activity', label: 'Activity', sortable: true },
        { key: 'details', label: 'Details', sortable: false },
        { key: 'created_by', label: 'Created By', sortable: true },
      ],
    };
  },
  components: {
    flatPickr,
    ToggleButton,
    'v-input-colorpicker': InputColorPicker,
    ReorderModal,
  },
  validations: {
    category: {
      categoryName: { required },
      network: { required },
      hierarchy: { required },
    },
  },
  computed: {
    ...liteEloadDropdownComputed,
    ...liteEloadCategoryComputed,
    canSubmit() {
        return this.categoryId > 0 ? this.eloadCategoryModule.access.update : this.eloadCategoryModule.access.create;
    },
    // disable status toggle when category is default or duration controls it
    disableStatus() {
        return this.category && Number(this.category.default_category) === 1 || this.isStatusDisabled;
    },
    // Map category logs from store with proper format
    categoryLogs() {
      const logs = this.liteCategoryLogs || [];
      return logs.map(log => {
        // Transform backend format to frontend format
        const changes = (log.changes || []).map(change => ({
          field: change.field,
          old: change.oldValue,
          new: change.newValue
        }));

        return {
          id: log.id,
          created_at: log.created_at,
          activity: log.remarks,
          details: log.remarks,
          reason: log.reason || null,
          created_by: log.actor,
          changes: changes
        };
      });
    },
  },
  watch: {
    'category.special'(val) {
      // Ensure UI updates and associated fields reset when toggled off
      if (!val) this.onSpecialToggleChange();
    },
  },
  
  async mounted() {
    this.categoryId = this.$route.params.id;
    this.changeTitle({ title: (this.categoryId > 0 ? 'Edit' : 'Add') + ' E-Load Category' });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: 'Peddlr', to: { name: 'Home' } },
        { text: 'Peddlr Lite Manager', to: { name: 'E-Load Lite Category Promo' } },
        { text: 'E-Load Category Form', to: { name: 'E-Load Lite Category Promo' } },
      ],
    });

    await this.loadNetworkOptions();

    if (this.categoryId > 0) {
      await this.editCategory();
    }

    await getModuleAccess('Products', 'eLoad', 'eLoad Category').then((access) => {
      this.eloadCategoryModule.access = access;
    });
  },
  methods: {
    ...layoutMethods,
    ...liteEloadCategoryMethods,
    ...erpUserRolesMethods,
    ...liteEloadDropdownMethods,

    onStatusToggle(state) {
        const newValue = typeof state === 'boolean' ? state : !!state?.value;
        const prevValue = !newValue;

        if (Number(this.category.default_category) === 1) {
            Swal.fire({
                icon: 'info',
                title: 'Cannot Change Status',
                html: 'This category is managed by Core Manager and cannot be deactivated.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#6f42c1',
            });

            this.$nextTick(() => { this.category.status = prevValue; });
            return;
        }

        const hasDuration = this.category.special && !!this.category.durationDateRange;
        const isExpired = this.category.duration === 'Expired';
        
        if (hasDuration && !isExpired) {
            Swal.fire({
                icon: 'info',
                title: 'Cannot Change Status',
                html: `Please clear the Duration field before changing the status. If the duration is set, it will automatically control the status based on the user-set duration.<br><br>
                        Once the duration starts, this category will become active, and when it ends, it will become inactive.`,
                confirmButtonText: 'OK',
                confirmButtonColor: '#6f42c1',
            });

            this.$nextTick(() => { this.category.status = prevValue; });
            return;
        }

        if (newValue === false && Number(this.category.itemCount) > 0) {
            Swal.fire({
                icon: 'info',
                title: 'Unable to Disable Category',
                html: `This category cannot be disabled because there is an active SKU assigned.<br><br>
                        Please move the SKU to another category before disabling this one.`,
                confirmButtonText: 'OK',
                confirmButtonColor: '#6f42c1',
            });

            this.$nextTick(() => { this.category.status = prevValue; });
            return;
        }

    },
    async loadNetworkOptions() {
      try {
        await this.fetchLiteNetworksList();
        if (this.getLiteNetworksList && this.getLiteNetworksList.length > 0) {
          const sorted = (this.getLiteNetworksList || []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name)));
          this.networkOptions = sorted.map((row) => ({ text: row.name, value: row.id }));
        }
      } catch (error) {
        console.log('error while loading networks', error);
      }
    },

    isFormInvalid() {
        this.submitted = true;
        this.$v.$touch();

        if (this.$v.$invalid) {
            return true;
        }

        if (Number(this.category.hierarchy) <= 0) {
            return true;
        }

        if (this.category.special && this.icon2.default && !this.icon2.url) {
            return true;
        }
        if (this.category.special && this.icon2.default && this.icon2.invalidFormat) {
            return true;
        }
        return false;
    },

    buildCategoryPayload() {
      const selectedNetwork = this.networkOptions.find((option) => option.value === this.category.network);
      const payload = {
        name: this.category.categoryName,
        status: this.category.status ? 1 : 0,
        networkId: this.category.network,
        network: selectedNetwork ? selectedNetwork.text : null,
        hierarchy: Number(this.category.hierarchy),
        isSpecial: this.category.special ? 1 : 0,
        proceedAction: this.proceedAction,
      };

      if (this.category.special) {
        payload.durationDays = this.category.durationDays ? Number(this.category.durationDays) : null;
        payload.durationDateRange = this.category.durationDateRange || null;
        const toIconKey = (url) => (url || '').replace(/^https?:\/\/[^/]+\//, '');
        const DEFAULT_ICON_URL = 'https://dev-crm-files.peddlr.io/eload-special-category/icons/default-icon.svg';
        const icon1Url = (this.icon1 && this.icon1.url) ? this.icon1.url : DEFAULT_ICON_URL;
        const icon2Url = (this.icon2 && this.icon2.url) ? toIconKey(this.icon2.url) : null;

        payload.icon = [
          icon1Url,
          icon2Url,
        ];
        payload.color = [
          this.color1.value || null,
          this.color2.value || null,
        ];
        // Separate flags: which icon/color is the active/default one (1 or 2)
        payload.defaultIcon  = this.icon1.default  ? 1 : 2;
        payload.defaultColor = this.color1.default ? 1 : 2;
      }

      if (this.categoryId > 0) {
        payload.id = this.categoryId.toString();
      }

      return payload;
    },

    resolveErrorMessage(error) {
      if (typeof error === 'string') {
        return error;
      }

      if (error && error.data && error.data.message) {
        return error.data.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return 'An error occurred while saving category.';
    },

    async saveCategory() {
      if (this.isFormInvalid()) {
        return;
      }

      const action = this.categoryId > 0 ? 'update-category' : 'create-category';

      // Upload icon2 to S3 before building payload
      let isIconUploaded = true;
      if (this.category.special && this.isForUploading && !this.icon1.default && this.icon2.url) {
        isIconUploaded = await this.uploadFile();
        if (isIconUploaded) {
          this.icon2.url = this.s3_url;
        }
      }

      if (!isIconUploaded) {
        Swal.fire('Icon Not Uploaded!', 'Error occurred while uploading icon 2. Please try again later.', 'error');
        this.isForUploading = false;
        return;
      }

      this.isForUploading = false;
      const payload = this.buildCategoryPayload();

      await this.actionLiteCategory({ action, body: payload })
        .then(() => {
          Swal.fire(
            `${action === 'create-category' ? 'Created' : 'Updated'} Successful!`,
            `${this.category.categoryName} category is successfully ${action === 'create-category' ? 'created' : 'updated'}.`,
            'success'
          );
          this.$router.push(this.$route.query.redirectFrom || { name: 'E-Load Lite Category Promo' });
        })
        .catch(async (error) => {
          // keep the raw error object when available
          const raw = error || {};
          const message = this.resolveErrorMessage(raw);
          const lowerMessage = String(message).toLowerCase();

          // Backend explicit hierarchy conflict payload: { statusCode: 409, status: 'hierarchy_conflict', message: '...' }
          const isHierarchyConflict = (raw && (raw.status === 'hierarchy_conflict' || raw.statusCode === 409 || raw.code === 409)) || (lowerMessage.includes('hierarchy') && (lowerMessage.includes('in use') || lowerMessage.includes('exist')));

          if (String(message).toLowerCase().includes('already exists') && String(message).toLowerCase().includes('category')) {
            await Swal.fire({
              icon: 'warning',
              title: 'Category Already Exists',
              html: 'A category with the same name and network already exists. Please use a different category name or select another network to continue.',
              confirmButtonText: 'OK',
            });
            return;
          }

          if (isHierarchyConflict) {
            const hierarchyValue = Number(this.category.hierarchy);
            const result = await Swal.fire({
              icon: 'warning',
              title: `Hierarchy #${hierarchyValue} Already Exists Within the Network`,
              html: `By proceeding, the existing hierarchy (#${hierarchyValue}) within the network will change to #${hierarchyValue + 1}, and the new hierarchy you created will become #${hierarchyValue}.`,
              showCancelButton: true,
              confirmButtonText: 'Proceed',
              cancelButtonText: 'Cancel',
              customClass: {
                confirmButton: 'btn btn-primary me-2',
                cancelButton: 'btn btn-danger',
              },
              buttonsStyling: false,
            });

            if (result.isConfirmed) {
              this.proceedAction = true;
              await this.saveCategory();
            }
            return;
          }

          Swal.fire(`${action === 'create-category' ? 'Create' : 'Update'} Failed!`, message, 'warning');
        });
    },

    setDefaultIcon(iconNum) {
      if (iconNum === 1) {
        this.icon1.default = true;
        this.icon2.default = false;
        return;
      }

      this.icon1.default = false;
      this.icon2.default = true;
    },

    setDefaultColor(colorNum) {
      if (colorNum === 1) {
        this.color1.default = true;
        this.color2.default = false;
        return;
      }

      this.color1.default = false;
      this.color2.default = true;
    },

    onFileChange(e) {
      const reader = new FileReader();
      // support both native event and b-form-file emitted value
      let files = null;
      if (e && e.target && e.target.files) files = e.target.files;
      else if (e && e.length) files = e;
      else if (this.iconFile) files = (this.iconFile instanceof File) ? [this.iconFile] : this.iconFile;

      if (!files || files.length === 0) {
        this.icon2Img = { filename: null, type: null, base64: null };
        return;
      }

      const file = files[0];

      reader.onloadend = () => {
        const base64Data = reader.result;
        this.icon2Img.base64 = new Buffer.from(
          base64Data.replace(/^data:image\/[a-zA-Z+]+;base64,/, ''),
          'base64'
        );
        this.icon2Img.type = base64Data.split(';')[0].split('/')[1];
        this.icon2Img.filename = `${shortid.generate()}.${this.icon2Img.type.replace('+xml', '')}`;
        this.icon2.url = base64Data;
        this.isForUploading = true;
        if (this.compareStr(this.icon2Img.type, ['svg+xml']) === false) {
            this.icon2.invalidFormat = true;
        } else {
            this.icon2.invalidFormat = false;
        }
        this.icon1.default = false;
        this.icon2.default = true;
      };
      reader.readAsDataURL(file);
    },

    compareStr(data, arr_similar) {
      for (let i = 0; i < arr_similar.length; i++) {
        if (data.toUpperCase() === arr_similar[i].toUpperCase()) return true;
      }
      return false;
    },

    async uploadFile() {
      let isUploaded = false;
      if (this.icon2Img.base64) {
        const icon2Img = { ...this.icon2Img };
        await this.uploadLiteSpecialCategoryIcon2(icon2Img)
          .then((imageKey) => {
            this.s3_url = imageKey;
            isUploaded = true;
          })
          .catch((err) => {
            Swal.fire('Upload Failed!', `${err}`, 'error');
          });
      }
      return isUploaded;
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async editCategory() {
      await this.actionLiteCategory({ action: 'get-category-details', body: { id: this.categoryId } })
        .then((res) => {
          const payload = res && res.message ? (res.message.category || res.message) : res;

          const data = payload || {};

          this.categoryId = data.id || data.categoryId || this.categoryId;
          this.category.categoryName = data.name || data.categoryName || '';
          this.category.status = (data.status == 1 || data.status == '1' || data.status === true);
          this.category.hierarchy = data.hierarchy || data.hierarchyOrder || data.order || '';
          this.category.special = data.isSpecial == 1 || data.isSpecial === true || data.special == '1' || data.special === true;
          this.category.durationDays = data.durationDays || data.duration || null;
          this.category.durationDateRange = data.durationDateRange || null;
          this.category.default_category = data.default_category || 0;

          // itemCount indicates assigned SKUs; keep for disable checks
          this.category.itemCount = Number(data.itemCount || data.item_count || 0);

          if (data.networkId) {
            this.category.network = data.networkId;
          } else {
            const existingNetwork = this.networkOptions.find((option) => option.text === (data.network || data.networkName));
            this.category.network = existingNetwork ? existingNetwork.value : null;
          }

          const defaultIcon1 = { name: 'icon1', url: 'https://dev-crm-files.peddlr.io/eload-special-category/icons/default-icon.svg', default: true };
          const defaultIcon2 = { name: 'icon2', url: '', default: false, invalidFormat: false };

          const savedIcon1 = data.icon[0];
          const savedIcon2 = data.icon[1];

          this.icon1 = {
            ...defaultIcon1,
            url: savedIcon1,
          };
          this.icon2 = {
            ...defaultIcon2,
            url: savedIcon2 || '',
          };

          // defaultIcon: 1 > icon1 is default, 2 > icon2 is default
          const defaultIconNum = data.defaultIcon || 1;
          this.icon1.default = defaultIconNum === 1;
          this.icon2.default = defaultIconNum === 2;

          // Restore colors from separate varchar columns or from color array
          const colorArr = Array.isArray(data.color) ? data.color : null;
          this.color1 = {
            name: 'color1',
            value: data.alterColor1 || (colorArr && colorArr[0]) || '#E83092',
            default: true,
          };
          this.color2 = {
            name: 'color2',
            value: data.alterColor2 || (colorArr && colorArr[1]) || '#7F4697',
            default: false,
          };

          // defaultColor: 1 > color1 is default, 2 > color2 is default
          const defaultColorNum = data.defaultColor || 1;
          this.color1.default = defaultColorNum === 1;
          this.color2.default = defaultColorNum === 2;
        })
        .catch((err) => {
          console.log('Error fetching category details', err);
        });
    },

    onSpecialToggleChange() {
      if (!this.category.special) {
        this.category.durationDays = null;
        this.category.durationDateRange = null;
        this.icon1.default = true;
        this.icon2.default = false;
        this.icon2.url = '';
        this.iconFile = null;
        this.icon2Img = { filename: null, type: null, base64: null };
        this.isForUploading = false;
        this.color1.default = true;
        this.color2.default = false;
      }
    },

    onDurationRangeChange(value) {
      this.category.durationDateRange = value;

      if (!value) {
        this.category.durationDays = null;
        return;
      }

      let startDate = null;
      let endDate = null;

      if (Array.isArray(value) && value.length === 2) {
        startDate = value[0];
        endDate = value[1];
      } else if (typeof value === 'string' && value.includes(' to ')) {
        const parts = value.split(' to ');
        startDate = parts[0];
        endDate = parts[1];
      }

      if (!startDate || !endDate) {
        this.category.durationDays = null;
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffMs = end.getTime() - start.getTime();

      if (Number.isNaN(diffMs) || diffMs < 0) {
        this.category.durationDays = null;
        return;
      }

      this.category.durationDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
      if (this.category.durationDays && this.category.status) {
        this.category.status = false;
      }
    },

    async formHandler() {
      this.proceedAction = false;
      await this.saveCategory();
    },

    viewIcon(iconNumber, iconUrl) {
      if (!iconUrl) {
        return;
      }

      Swal.fire({
        title: iconNumber == 1 ? `<p>Icon 1<span style="color: #ACACAC;"> (default)</span></p>` : `<p>Icon 2</p>`,
        html: `<img src=${iconUrl} height='180' width='180'>`,
        width: 320,
        showConfirmButton: false,
      });
    },

    // Change Logs Methods
    async getCategoryLogs() {
      const params = {
        categoryId: Number(this.categoryId),
        page: this.isSearching ? 1 : this.searchValue.page,
        rows: this.searchValue.rows,
        sort: this.sortBy || 'created_at',
        orderDesc: this.sortDesc,
      };

      // Add search params if present
      if (this.searchValue.search && this.searchValue.search.trim()) {
        params.search = this.searchValue.search.trim();
        params.searchField = this.searchValue.searchField || null;
      }

      // Add date range if present
      if (this.searchValue.dateRange) {
        params.dateRange = this.searchValue.dateRange;
      }

      try {
        await this.getLiteCategoryAuditLogs(params);
        this.isSearching = false;
        if (!this.showLogsModal) {
          this.showLogsModal = true;
        }
      } catch (error) {
        console.error('Error fetching category logs:', error);
        this.$toastV2.error('Failed to load category logs', 3000, 'bottom-right');
      }
    },

    async sortList(action) {
      this.sortBy = action.sortBy;
      this.sortDesc = action.sortDesc;
      await this.getCategoryLogs();
    },

    onModalOpen() {
      this.searchValue.searchField = 'all';
      this.searchValue.search = '';
      this.searchValue.dateRange = '';
      this.searchValue.page = 1;
      this.searchValue.rows = 10;
      this.getCategoryLogs();
    },

    formatFieldName(fieldName) {
      const fieldNameMap = {
        'Category Name': 'Category Name',
        'Network': 'Network',
        'Hierarchy': 'Hierarchy',
        'Category Status': 'Status',
        'Special Category': 'Special Category',
        'Alter Icon': 'Alter Icon',
        'Alter Color': 'Alter Color',
        'Duration (# of Days)': 'Duration (# of Days)',
      };
      return fieldNameMap[fieldName] || fieldName;
    },

    formatChangeValue(value, fieldName) {
      if (value === null || value === undefined) {
        return '-';
      }

      if (Array.isArray(value)) {
        return value.join(', ');
      }

      // Format dates if needed
      if (fieldName === 'Duration (# of Days)' && value && moment(value).isValid()) {
        return moment(value).format('YYYY-MM-DD hh:mm A');
      }

      return value;
    },

    openChangePreview(item, event) {
      if (this._changePreviewOutsideHandler) {
        document.removeEventListener('click', this._changePreviewOutsideHandler);
        this._changePreviewOutsideHandler = null;
      }

      this.changePreviewData = item;
      this.changePreviewVisible = true;

      this.$nextTick(() => {
        this._changePreviewOutsideHandler = (e) => {
          const panel = document.querySelector('.change-preview');
          if (!panel || !this.changePreviewVisible) return;

          const isClickInside = panel.contains(e.target);
          const isViewChangesLink = e.target.closest('.view-link');
          if (!isClickInside && !isViewChangesLink) {
            this.closeChangePreview();
          }
        };
        document.addEventListener('click', this._changePreviewOutsideHandler, true);
      });

      if (event) {
        event.stopPropagation();
      }
    },

    closeChangePreview() {
      this.changePreviewVisible = false;
      this.changePreviewData = null;

      if (this._changePreviewOutsideHandler) {
        document.removeEventListener('click', this._changePreviewOutsideHandler, true);
        this._changePreviewOutsideHandler = null;
      }
    },

    async onExport() {
      const params = {
        categoryId: Number(this.categoryId),
        email: JSON.parse(localStorage.getItem('userData')).email,
      };

      if (this.searchValue.search && this.searchValue.search.trim()) {
        params.search = this.searchValue.search.trim();
        params.searchField = this.searchValue.searchField || null;
      }

      if (this.searchValue.dateRange) {
        params.dateRange = this.searchValue.dateRange;
      }

      try {
        await this.exportLiteCategoryAuditLogs(params);
        Swal.fire('Success!', 'Your request for reports is pending. It will be sent to your email once it\'s ready.', 'success');
      } catch (error) {
        console.error('Error exporting logs:', error);
        Swal.fire('Failed!', 'Your request for reports has failed.', 'warning');
      }
    },

    showReorderDialog() {
      this.showReorderModal = true;
    },

    reOrderConfirm(newHierarchy) {
      this.category.hierarchy = newHierarchy;
      this.showReorderModal = false;
    },

    reOrderClose() {
      this.showReorderModal = false;
    },

    goBack() {
      this.$router.push({ name: 'E-Load Lite Category Promo' });
    },
  },
};
</script>

<template>
  <div class="row">
    <div class="col-xl-9 col-lg-8 col-md-10">
      <div class="card category-form-card">
        <div class="card-body">
          <form class="px-md-3 py-2" @submit.prevent="formHandler()">
            <label v-if="(categoryId <= 0 || isNaN(categoryId)) && !eloadCategoryModule.access.create" class="form-label medium fw-bold text-danger mb-3">Note: Your access is limited. You cannot create a category.</label>
            <label v-else-if="categoryId > 0 && !eloadCategoryModule.access.update" class="form-label medium fw-bold text-danger mb-3">Note: Your access is limited. You cannot update a category.</label>

            <div class="mb-4 d-flex align-items-center gap-2">
              <toggle-button
                :height="20"
                :width="40"
                v-model="category.status"
                :sync="true"
                :disabled="disableStatus"
                color="#7F4697"
                @change="onStatusToggle"
                />
              <label class="form-label mb-2">Active</label>
            </div>

            <div v-if="Number(category.default_category) === 1" class="default-note mb-4">
              <strong>Note:</strong> This category is managed by Core Manager therefore, you cannot change the network or deactivate it.
            </div>

            <div class="mb-4">
              <label class="form-label">Category Name<span class="text-danger">*</span></label>
              <input
                v-model="category.categoryName"
                type="text"
                class="form-control"
                placeholder="Enter Category Name"
                :class="{ 'is-invalid': submitted && $v.category.categoryName.$error }"
              />
              <div class="invalid-feedback" v-if="submitted && $v.category.categoryName.$error">Category Name is required.</div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-4">
                <label class="form-label">Network<span class="text-danger">*</span></label>
                <b-form-select
                  v-model="category.network"
                  :options="networkOptions"
                  class="form-select rounded-0 rounded-start shadow-none"
                  value-field="value"
                  text-field="text"
                  :class="{ 'is-invalid': submitted && $v.category.network.$error }"
                  :disabled="category.default_category == 1 ? true : false"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>Select Network</b-form-select-option>
                  </template>
                </b-form-select>
                <div class="invalid-feedback" v-if="submitted && $v.category.network.$error">Network is required.</div>
              </div>

              <div class="col-md-6 mb-4">
                <label class="form-label">Hierarchy<span class="text-danger">*</span></label>
                <div class="d-flex align-items-center gap-2">
                  <input
                    v-model="category.hierarchy"
                    type="number"
                    min="1"
                    class="form-control"
                    placeholder="0"
                    :class="{ 'is-invalid': submitted && ($v.category.hierarchy.$error || Number(category.hierarchy) <= 0) }"
                  />
                  <button
                    v-if="this.categoryId > 0"
                    type="button"
                    class="btn btn-sm d-flex align-items-center justify-content-center p-0"
                    style="width: 32px; height: 28px; background-color: #E83092; border: none;"
                    @click="showReorderDialog"
                    v-b-tooltip.hover.top="'Click to reorder hierarchy'"
                  >
                    <i class="mdi mdi-playlist-edit text-white fs-4"></i>
                  </button>
                  
                </div>
                <div class="invalid-feedback d-block" v-if="submitted && $v.category.hierarchy.$error">Hierarchy is required.</div>
                <div class="invalid-feedback d-block" v-else-if="submitted && !$v.category.hierarchy.$error && Number(category.hierarchy) <= 0">Hierarchy must be greater than 0.</div>
              </div>
            </div>

            <div v-if="Number(category.default_category) !== 1" class="mb-2 d-flex align-items-center gap-2">
              <label class="form-label mb-0">Special Category</label>
              <div class="form-check form-switch form-switch-md mb-0" dir="ltr">
                <input id="special-switch" v-model="category.special" :true-value="true" :false-value="false" class="form-check-input" type="checkbox" @change="onSpecialToggleChange" />
              </div>
            </div>

            <div v-if="category.special" class="special-category-section mt-4">
              <div class="row">
                <div class="col-md-6 mb-4">
                  <label class="form-label">
                    Alter Icon 1
                    <span class="text-default">(default)</span>
                    <span class="text-danger">*</span>
                    <a href="#" class="small text-decoration-underline ms-1" @click.prevent="viewIcon(1, icon1.url)">view icon</a>
                  </label>
                  <div class="d-flex align-items-center gap-2">
                    <input class="form-check-input custom-radio" type="radio" name="default-icon" :checked="icon1.default" @click="setDefaultIcon(1)" />
                    <img v-if="icon1.url" :src="icon1.url" class="alter-icon" alt="Alter icon 1" />
                  </div>
                </div>

                <div class="col-6">
                  <label class="form-label">
                    Alter Icon 2<span class="text-danger">*</span>  
                    <b-link v-if="icon2.url" href="#" class="text-decoration-underline small ms-1" @click="viewIcon(2, icon2.url)">view icon</b-link>
                  </label>
                  <div class="form-check d-flex align-items-center">
                    <input
                      class="form-check-input custom-radio me-2"
                      type="radio"
                      name="iconRadio"
                      :checked="icon2.default"
                      @click="setDefaultIcon(2)"
                    />
                    <!-- <div @click="triggerFileInput" v-if="icon2.url">
                      <input
                        style="display: none"
                        type="file"
                        accept=".svg"
                        ref="fileInput"
                        @change="onFileChange"
                      />
                      <b-img
                        v-b-tooltip.hover.right="'Click to change icon 2'"
                        :src="icon2.url"
                        class="custom-icon me-2"
                      />
                    </div> -->
                    <div style="width:250px; display:flex; align-items:center; gap:8px;">
                      <b-form-file
                        type="file"
                        class="form-control mb-1 file-input"
                        :class="{ 'is-invalid': submitted && icon2.default && !icon2.url || (icon2.default && icon2.invalidFormat) }"
                        accept=".svg"
                        v-model="iconFile"
                        @change="onFileChange"
                        plain
                      />
                    </div>
                  </div>
                  <div class="invalid-feedback d-block" v-if="submitted && icon2.default && !icon2.url">Icon 2 is required if it's set to default.</div>
                  <label class="small mb-0" :class="icon2.default &&icon2.invalidFormat ? 'text-danger' : 'text-muted'">File Format: Must be in SVG format.</label>
                </div>
              </div>

              <div class="row mt-2">
                <div class="col-md-6 mb-4">
                  <label class="form-label">Alter Color 1 <span class="text-danger">*</span></label>
                  <div class="form-check d-flex align-items-center">
                    <input
                      class="form-check-input custom-radio me-2"
                      type="radio"
                      name="colorRadio"
                      :checked="color1.default"
                      @click="setDefaultColor(1)"
                    />
                    <v-input-colorpicker v-model="color1.value" />
                  </div>
                </div>
                <div class="col-md-6 mb-4">
                  <label class="form-label">Alter Color 2 <span class="text-danger">*</span></label>
                  <div class="form-check d-flex align-items-center">
                    <input
                      class="form-check-input custom-radio me-2"
                      type="radio"
                      name="colorRadio"
                      :checked="color2.default"
                      @click="setDefaultColor(2)"
                    />
                    <v-input-colorpicker v-model="color2.value" />
                  </div>
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label">Duration (# of Days)</label>
                <flat-pickr
                  v-model="category.durationDateRange"
                  class="form-control"
                  :config="durationRangeOptions"
                  placeholder="Select a date duration"
                  @input="onDurationRangeChange"
                ></flat-pickr>
              </div>

              <p class="mb-2 text-muted">Note: When a duration is set, the Status toggle will be automatically disabled. The duration will control when this item becomes active or inactive.</p>
            </div>

            <!-- Change Log Link -->
            <div v-if="categoryId > 0" class="mb-3">
              <p class="mb-0">
                <span class="fw-semibold mx-1">Change Log:</span> 
                <a href="#" @click.prevent="onModalOpen" class="view-link">View</a>
              </p>
            </div>

            <div class="d-flex justify-content-end mt-4 gap-2">
              <b-button :disabled="!canSubmit" variant="primary" class="w-sm px-4" type="submit">{{ categoryId > 0 ? 'Update' : 'Save' }}</b-button>
              <b-button variant="light" class="w-sm px-4" type="button" @click="goBack">Cancel</b-button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Category Logs Modal -->
    <b-modal
      v-model="showLogsModal"
      id="category-logs-modal"
      title="Category Logs"
      size="xl"
      hide-header-close
      centered
      modal-class="category-log-modal"
    >
      <template #modal-title>
        Category Logs
      </template>
      <template #modal-footer>
        <div class="w-100 text-end">
          <b-button variant="light" class="px-5" @click="showLogsModal = false">Close</b-button>
        </div>
      </template>

      <div class="row align-items-start g-0 mt-2 mb-3">
        <div class="col-sm-2">
          <b-form-group label-for="search-type" class="text-end">
            <b-form-select
              v-model="searchValue.searchField"
              class="form-select rounded-0 shadow-none w-100"
              :options="searchLogOption"
            >
            </b-form-select>
          </b-form-group>
        </div>

        <div class="col-sm-3">
          <b-form-group label-for="search-keyword" description="Search Keyword" class="text-end">
            <input
              class="form-control rounded-0 shadow-none w-100"
              v-model="searchValue.search"
              placeholder="Search Keyword"
              @keydown.enter.prevent="getCategoryLogs"
            />
          </b-form-group>
        </div>

        <div class="col-sm-3 ms-3">
          <b-form-group label-for="date-range" description="Filter by date" class="text-end">
            <flat-pickr
              v-model="searchValue.dateRange"
              :config="rangeOptions"
              placeholder="Select date range"
              class="form-control rounded-0 shadow-none w-100"
            ></flat-pickr>
          </b-form-group>
        </div>

        <div class="col d-flex justify-content-end align-items-center buttons-group gap-1 flex-wrap">
          <b-button variant="primary" type="button" @click="getCategoryLogs" class="h-fit d-flex align-items-center justify-content-center me-2 w-fit">
            <i class="bx bx-search me-1 mt-1"></i>Search
          </b-button>

          <b-button variant="primary" @click="onExport" type="button" class="px-3 d-flex align-items-center">
            <i class='uil uil-export me-2'></i>
            Export
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <!-- table responsive -->
          <div class="table-responsive">
            <div class="table align-middle table-nowrap">
              <b-table
                :items="categoryLogs"
                :fields="fields"
                responsive="sm"
                :current-page="searchValue.page"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :no-local-sorting="true"
                @sort-changed="sortList"
              >
                <template #cell(details)="data">
                  <span v-if="data.item.reason" :id="'reason-' + data.item.id" class="d-inline-block">
                    <a href="#" class="view-link" @click.prevent="void 0">View reason</a>
                    <b-tooltip
                      :target="'reason-' + data.item.id"
                      triggers="hover focus"
                      placement="top"
                      boundary="viewport"
                      :title="data.item.reason">
                    </b-tooltip>
                  </span>
                  <a v-else-if="data.item.changes && data.item.changes.length > 0" href="#" class="view-link" @click.prevent="openChangePreview(data.item, $event)">View changes</a>
                </template>
                
                <template #cell(created_at)="data">
                  <span>{{ data.item.created_at | formatDate }}</span>
                </template>
              </b-table>
            </div>
          </div>
          
          <!-- change preview floating panel -->
          <div v-if="changePreviewVisible" class="change-preview">
            <div class="change-preview__inner">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <h6 class="mb-0 text-white fw-bold">Edited Fields:</h6>
                <button class="btn-close" @click="closeChangePreview">&times;</button>
              </div>
              <!-- show changePreviewData.changes array as Before/After rows -->
              <div v-if="changePreviewData && changePreviewData.changes && changePreviewData.changes.length > 0">
                <table class="table table-sm table-change mb-0">
                  <thead>
                    <tr>
                      <th style="width: 35%">Field</th>
                      <th style="width: 65%">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, idx) in changePreviewData.changes" :key="idx">
                      <td class="align-top text-white">{{ formatFieldName(c.field) }}</td>
                      <td>
                        <div class="before-line text-white">
                          Before:
                          <span class="ms-1">{{ formatChangeValue(c.old, c.field) }}</span>
                        </div>
                        <div class="after-line text-white mt-2">
                          After:
                          <span class="ms-1">{{ formatChangeValue(c.new, c.field) }}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-white text-center py-3">
                <p class="mb-0">No detailed changes available.</p>
              </div>
            </div>
          </div>
          
          <div class="row g-0 text-center" v-if="liteCategoryLogsTotal == 0">
            <span>No data to display.</span>
          </div>
          
          <div class="row g-0 align-items-center" v-if="liteCategoryLogsTotal > 0">
            <div class="col-auto">
              <div class="d-flex align-items-center">
                <label class="me-2 mb-0 col-form-label">Display</label>
                <b-form-select 
                  v-model="searchValue.rows" 
                  :options="pageOptions" 
                  class="form-select shadow-none w-fit" 
                  @change="getCategoryLogs"
                ></b-form-select>
              </div>
            </div>
            <div class="col" v-if="liteCategoryLogsTotal > searchValue.rows">
              <div class="float-end">
                <b-pagination
                  class="pagination-rounded mb-0"
                  v-model="searchValue.page"
                  @input="getCategoryLogs"
                  :total-rows="liteCategoryLogsTotal"
                  :per-page="searchValue.rows"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div><!--end of col-12-->
      </div><!--end of row-->
    </b-modal>
    <!-- END Category Logs Modal -->

    <!-- Reorder Modal -->
    <ReorderModal
      :visible="showReorderModal"
      :category-name="category.categoryName"
      :current-hierarchy="category.hierarchy || 1"
      @confirm="reOrderConfirm"
      @close="reOrderClose"
    />
  </div>
</template>

<style scoped>
.category-form-card {
  border-radius: 12px;
}

.special-category-section {
  border-top: 1px solid #f0f0f0;
  padding-top: 18px;
}
.custom-icon {
  height: 50px;
  width: 50px;
}
.form-check-input:checked {
    background-color: #E83092;
    border-color: #E83092;
}

.custom-radio {
  height: 23px;
  width: 23px;
}
.alter-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.icon-upload {
  max-width: 260px;
}

.form-check-input:checked {
  background-color: #E83092;
  border-color: #E83092;
}
input.is-invalid::file-selector-button  {
    color: var(--colors-error-error-500, #9A211F);
    font-family: Roboto;
    font-style: normal;
    background: #F6DFDF;
}

/* Change logs modal styles */
/* Change logs modal styles (copied from maintenance design) */
.view-link {
  color: #e935a1;
  font-weight: 600;
  text-decoration: underline !important;
}
.view-link:hover {
  text-decoration: underline;
}

.change-preview {
  position: fixed;
  left: 50%;
  top: 18%;
  transform: translateX(-50%);
  z-index: 2050;
  background: transparent;
  padding: 0;
  pointer-events: none;
}

.change-preview__inner {
  background: #0f1112 !important;
  border-radius: 12px;
  padding: 22px 26px;
  width: 720px;
  max-width: 94vw;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.04);
  pointer-events: auto;
}

.table-change thead th {
  background: #0f1112 !important;
  color: #ffffff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.913);
  font-weight: 700;
  font-size: 14px;
  padding: 10px 12px;
  text-align: left;
}

.table-change td {
  background: #0f1112 !important;
  color: #e6e6e6 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.654);
  padding: 11px 12px;
  font-size: 13px;
  line-height: 1.4;
}
.text-default {
  color: #ACACAC;
}

.table-change tbody tr:last-child td {
  border-bottom: none;
}

.table-change tbody tr:hover {
  background: rgba(255, 255, 255, 0.02);
}

.change-preview__inner .table-change td {
  padding: 8px 18px;
  vertical-align: top;
}

.change-preview__inner .table-change thead th {
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.12);
}

.change-preview__inner .before-line strong,
.change-preview__inner .after-line strong {
  color: #e6e6e6;
}

.btn-close {
  background: transparent;
  border: 0;
  color: #ffffff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.12s;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover { 
  opacity: 1; 
}

::v-deep .category-log-modal .modal-content {
  border-radius: 12px;
  padding: 22px 24px;
}

::v-deep .category-log-modal .modal-dialog {
  max-width: 900px;
  width: 100%;
}

@media (max-width: 992px) {
  ::v-deep .category-log-modal .modal-dialog { 
    max-width: 720px; 
  }
}

@media (max-width: 768px) {
  ::v-deep .category-log-modal .modal-dialog { 
    max-width: 95vw; 
    margin: 0.5rem; 
  }
  ::v-deep .category-log-modal .modal-content { 
    padding: 14px; 
  }
}

@media (max-width: 576px) {
  .category-log-modal .row.align-items-start.g-0 > .col-sm-2,
  .category-log-modal .row.align-items-start.g-0 > .col-sm-3,
  .category-log-modal .row.align-items-start.g-0 > .col-sm-1 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .category-log-modal .b-form-group.text-end {
    text-align: left !important;
  }

  .category-log-modal .ms-3 { 
    margin-left: 0 !important; 
  }

  .category-log-modal .buttons-group {
    justify-content: flex-end;
  }

  .category-log-modal .buttons-group .btn {
    white-space: nowrap;
  }
}

.logs-controls {
  gap: 8px;
  flex-wrap: wrap;
}

.logs-controls .form-control,
.logs-controls .form-select {
  height: 40px;
  border-radius: 8px;
}

::v-deep .table thead th {
  font-weight: 600;
  color: #111827;
}

::v-deep .table tbody td {
  color: #111827;
  vertical-align: middle;
}

::v-deep .pagination-rounded .page-item.active .page-link {
  background: #804997;
  border-color: #804997;
}

::v-deep .btn-outline-primary:hover,
::v-deep .btn-export-logs:hover {
  background: #e935a1;
  border-color: #e935a1;
  color: #ffffff;
}

::v-deep .modal-footer {
  border-top: none !important;
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
}

.default-note {
  border-radius: 4px;
  border: 1px solid var(--primary-color-primary-100, #9F85AD);
  background: var(--colors-primary-primary-50, #F2EDF5);
  padding: 12px 14px;
  color: #2b2b2b;
}
</style>